import { Table, Box, SpaceBetween, Link, PropertyFilter, TextFilter, Pagination } from '@amzn/awsui-components-react/polaris';
import React, { useEffect, useMemo, useState } from 'react';
import TaskStatusIndicator from '../shared/TaskStatusIndicator';
import CONSTANTS from 'src/utils/constants';
import TPENavigator from '../TPENavigator';
import DASHBOARD_CONSTANTS from './dashboardConstants';
import { OpenApproval, OpenApprovalsResult } from 'src/models/dashboard/OpenApproval';
import { convertObjectsToFilterOptions, getDefaultDateFilterOperators, OPERATORS_EQUALS_ONLY, OPERATORS_FOR_STRING_PROPERTY } from './helpers/PropertyFilterHelper';
import { PropertyFilterQuery } from '@amzn/awsui-collection-hooks';
import { DbSortOrder } from 'src/models/common/TPESearchRequest';
import { formatAccountingDueDate } from 'src/components/accounting-calendar/mecDayLabel';

export default function OpenApprovalsGrid(props: { 
    openApprovalsResult: OpenApprovalsResult, 
    isLoading: boolean, 
    dashboardView?: boolean,
    gridHeader?: any,
    showFilter?: boolean,
    onFilterTextChanged?: (query:PropertyFilterQuery) => void,
    onSortingChanged?: (sortOrder: string, sortField?:string) => void
 }) {
    const { openApprovalsResult, isLoading, dashboardView = false, gridHeader, showFilter = false, onFilterTextChanged, onSortingChanged } = props;

    const [navigateURL, setNavigateURL] = useState('');
    const [sortingDescending, setSortingDescending] = useState<boolean|undefined>(false);
    const [sortingColumn, setSortingColumn] = useState<any>(null);

    const handleNavigation = (calculationNumber: string, recordId: string | undefined, entityType?: string) => {
        if (entityType === DASHBOARD_CONSTANTS.ENTITY_TYPES.MULTIDIMENSIONAL_ALLOCATION_SHEET) {
            setNavigateURL(CONSTANTS.PAGE_NAV.MULTIDIMENSIONAL_ALLOCATION_SHEET_DETAILS.URL + '/' + recordId);
        } else if (entityType === DASHBOARD_CONSTANTS.ENTITY_TYPES.MULTIDIMENSIONAL_ALLOCATION_WORKSHEET) {
            setNavigateURL(CONSTANTS.PAGE_NAV.MULTIDIMENSIONAL_ALLOCATION_CALCULATION_BUILDER.URL + '/' + recordId);
        } else {
            navigateToCalcBuilder(calculationNumber, recordId);
        }
    }

    const navigateToCalcBuilder = (calculationNumber: string, recordId: string | undefined) => {
    if (recordId != null) {
        setNavigateURL(CONSTANTS.PAGE_NAV.CALCULATION_BUILDER.URL + '/' + btoa(recordId));
    } else {
        setNavigateURL(CONSTANTS.PAGE_NAV.ICRS_CALCULATION_BUILDER.URL + '/' + btoa(calculationNumber));
    }
    }

    const colDefs: any[] = useMemo(() => [
        {
            id: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ENTITY_ID.ACCESSOR,
            header: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ENTITY_ID.HEADER,
            cell: (item: OpenApproval) => <Link href="#" onFollow={() => handleNavigation(item.entityId, item.recordId, item.entityType)}>{item.entityId}</Link>,
            width: '20%',
            sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ENTITY_ID.ACCESSOR
        },
        {
            id: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ENTITY_TYPE.ACCESSOR,
            header: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ENTITY_TYPE.HEADER,
            cell: (item: OpenApproval) => (DASHBOARD_CONSTANTS.ENTITY_TYPE_TO_DISPLAY_MAP as any)[item.entityType],
            width: '20%',
            sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ENTITY_TYPE.ACCESSOR
        },
        {
            id: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.TASK_STATUS.ACCESSOR,
            header: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.TASK_STATUS.HEADER,
            cell: (item: OpenApproval) => <TaskStatusIndicator status={item.taskStatus} />,
            width: '20%',
            sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.TASK_STATUS.ACCESSOR
        },
        {
            id: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ACCOUNTING_DAY.ACCESSOR,
            header: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ACCOUNTING_DAY.HEADER,
            cell: (item: OpenApproval) => formatAccountingDueDate(item.dueDateWorkDayNumber, item.dueDateDisplay),
            width: '20%',
            sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ACCOUNTING_DAY.ACCESSOR
        },
        {
            id: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.DUE_DATE.ACCESSOR,
            header: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.DUE_DATE.HEADER,
            cell: (item: OpenApproval) => item.dueDateDisplay,
            width: '20%',
            sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.DUE_DATE.ACCESSOR
        },
        {
            id: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.REQUESTER.ACCESSOR,
            header: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.REQUESTER.HEADER,
            cell: (item: OpenApproval) => item.requester,
            width: '20%',
            sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.REQUESTER.ACCESSOR
        }
    ], [openApprovalsResult]);

    const filteringProperties = useMemo(() => [
        {
            key: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ENTITY_ID.ACCESSOR,
            operators: OPERATORS_FOR_STRING_PROPERTY,
            propertyLabel: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ENTITY_ID.HEADER,
            groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ENTITY_ID.HEADER} values`
        },
        {
            key: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ENTITY_TYPE.ACCESSOR,
            operators: OPERATORS_FOR_STRING_PROPERTY,
            propertyLabel: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ENTITY_TYPE.HEADER,
            groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.ENTITY_TYPE.HEADER} values`
        },
        {
            key: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.TASK_STATUS.ACCESSOR,
            operators: OPERATORS_FOR_STRING_PROPERTY,
            propertyLabel: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.TASK_STATUS.HEADER,
            groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.TASK_STATUS.HEADER} values`
        },
        {
            key: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.DUE_DATE.ACCESSOR,
            propertyLabel: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.DUE_DATE.HEADER,
            groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.DUE_DATE.HEADER} values`,
            operators: getDefaultDateFilterOperators()
        },
        {
            key: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.REQUESTER.ACCESSOR,
            operators: OPERATORS_FOR_STRING_PROPERTY,
            propertyLabel: DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.REQUESTER.HEADER,
            groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_APPROVAL_FIELDS.REQUESTER.HEADER} values`
        }
      ], []);

    const filteringOptions = useMemo(
          () => convertObjectsToFilterOptions(openApprovalsResult?.items || [])
          ,[openApprovalsResult])
      
    const [filterQuery, setFilteringQuery] = useState({
        tokens: [],
        operation: "and"
    } as PropertyFilterQuery);
    
    useEffect(() => {
        if (onFilterTextChanged){
            onFilterTextChanged(filterQuery);
        }
    }, [filterQuery])

    const items = openApprovalsResult?.items || [];

    return (
    <>
    <TPENavigator path={navigateURL} />
    <Table
        columnDefinitions={colDefs}
        enableKeyboardNavigation
        wrapLines={true}
        items={items}
        loadingText="Loading"
        loading={isLoading}
        header={gridHeader}
        sortingDescending={sortingDescending}
        sortingColumn={sortingColumn}
        empty={
            <Box
            margin={{ vertical: "xs" }}
            textAlign="center"
            color="inherit"
            >
            <SpaceBetween size="m">
                <b>No open approvals</b>
                <span>All approvals have been completed</span>
            </SpaceBetween>
            </Box>
        }
        pagination={
            openApprovalsResult?.pageSize === DASHBOARD_CONSTANTS.SMALL_PAGE_SIZE? null : <Pagination currentPageIndex={openApprovalsResult?.page} pagesCount={openApprovalsResult?.pagesCount} />
        }
        variant={dashboardView ? 'embedded' : 'container'}
        filter={ showFilter &&
            <PropertyFilter
                filteringPlaceholder="Find Approvals"
                expandToViewport
                query={filterQuery}
                onChange={({detail}) => setFilteringQuery(detail)}
                countText={isLoading? '' :`${items.length} match${items.length>1? 'es' : ''}`}
                filteringProperties={filteringProperties as any[]}
                filteringOptions={filteringOptions}
            />
        }
        onSortingChange={({ detail }) => {
            setSortingDescending(detail.isDescending);
            setSortingColumn(detail.sortingColumn);
            if (onSortingChanged){
                onSortingChanged(detail.isDescending? DbSortOrder.DESCENDING: DbSortOrder.ASCENDING, detail.sortingColumn.sortingField);
            }
        } }
        />
        </>
    )
}